import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';

export type LeafletStyle = 'color' | 'plain';

export interface LeafletOptions {
  /** Full share link (includes the secret key in the fragment). */
  link: string;
  /** Optional album name shown on page 1. */
  albumName?: string;
  /** Album id (d-tag) shown on page 1. */
  albumId: string;
  /** Optional unencrypted cover image URL (only used in color mode). */
  coverUrl?: string;
  /** Hue (0-359) derived from the album id. */
  hue: number;
  style: LeafletStyle;
}

const W = 148; // A5 width in mm
const H = 210; // A5 height in mm

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let rgb: [number, number, number] = [0, 0, 0];
  if (h < 60) rgb = [c, x, 0];
  else if (h < 120) rgb = [x, c, 0];
  else if (h < 180) rgb = [0, c, x];
  else if (h < 240) rgb = [0, x, c];
  else if (h < 300) rgb = [x, 0, c];
  else rgb = [c, 0, x];
  return [Math.round((rgb[0] + m) * 255), Math.round((rgb[1] + m) * 255), Math.round((rgb[2] + m) * 255)];
}

interface LoadedImage {
  dataUrl: string;
  width: number;
  height: number;
  format: 'JPEG' | 'PNG';
}

async function loadImage(url: string): Promise<LoadedImage> {
  // Some Blossom servers don't send CORS headers — fall back to the CORS proxy.
  let blob: Blob;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    blob = await res.blob();
  } catch {
    const res = await fetch(`https://proxy.shakespeare.diy/?url=${encodeURIComponent(url)}`);
    if (!res.ok) throw new Error('Failed to fetch cover image');
    blob = await res.blob();
  }
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(new Error('Failed to read cover image'));
    r.readAsDataURL(blob);
  });
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('Failed to decode cover image'));
    i.src = dataUrl;
  });
  // jsPDF cannot embed WebP/AVIF/etc. — re-encode to JPEG via canvas.
  // Drawing from a data URL keeps the canvas untainted.
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth || 1;
  canvas.height = img.naturalHeight || 1;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unavailable');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);
  return {
    dataUrl: canvas.toDataURL('image/jpeg', 0.92),
    width: canvas.width,
    height: canvas.height,
    format: 'JPEG',
  };
}

function groupId(id: string): string {
  return id.replace(/(.{4})(?=.)/g, '$1 ');
}

/**
 * jsPDF's built-in fonts (WinAnsi/Latin-1) cannot render emoji, CJK, or most
 * Unicode punctuation. Map common punctuation to ASCII, keep Latin-1 accents
 * (umlauts etc.), and drop everything else.
 */
function pdfSafe(text: string): string {
  const map: Record<string, string> = {
    '–': '-',
    '—': '-',
    '―': '-',
    '‘': "'",
    '’': "'",
    '‚': "'",
    '“': '"',
    '”': '"',
    '„': '"',
    '…': '...',
    '•': '*',
    '‹': '<',
    '›': '>',
    '€': 'EUR',
    ' ': ' ',
  };
  let out = '';
  for (const ch of text) {
    const mapped = map[ch] ?? ch;
    for (const m of mapped) {
      const code = m.codePointAt(0)!;
      if ((code >= 0x20 && code <= 0x7e) || (code >= 0xa0 && code <= 0xff)) out += m;
    }
  }
  return out;
}

/**
 * Generate a 4-page A5 leaflet PDF and trigger a download.
 *
 * Page 1: cover with album name, album ID and (in color mode) the cover image.
 * Page 2: instructions for the recipient.
 * Page 3: QR code of the full link.
 * Page 4: notes page with security reminder.
 */
export async function generateLeaflet(opts: LeafletOptions): Promise<void> {
  const colored = opts.style === 'color';
  const accent = hslToRgb(opts.hue, 0.6, 0.38);
  const light = hslToRgb(opts.hue, 0.55, 0.9);
  const dark = hslToRgb(opts.hue, 0.45, 0.25);
  const ink: [number, number, number] = colored ? accent : [0, 0, 0];
  const doc = new jsPDF({ unit: 'mm', format: 'a5', orientation: 'portrait' });

  // ---------- Page 1: cover ----------
  const bandH = 32;
  const bandTop = H - bandH;

  if (colored) {
    doc.setFillColor(...accent);
    doc.rect(0, 0, W, H, 'F');
    doc.setFillColor(...light);
    doc.rect(0, bandTop, W, bandH, 'F');
  } else {
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.6);
    doc.rect(10, 10, W - 20, H - 20);
  }

  const onAccent: [number, number, number] = colored ? [255, 255, 255] : [0, 0, 0];
  const onBand: [number, number, number] = colored ? dark : [0, 0, 0];

  doc.setTextColor(...onAccent);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  // Manual letter-spacing: jsPDF's charSpace option mis-centers text.
  doc.text('LOCKET ALBUM'.split('').join(' '), W / 2, 26, { align: 'center' });

  const name = pdfSafe(opts.albumName?.trim() || 'Private Album');
  doc.setFontSize(26);
  const nameLines = doc.splitTextToSize(name, W - 32) as string[];
  const lineH = 11;

  let drewCover = false;
  if (colored && opts.coverUrl) {
    try {
      const img = await loadImage(opts.coverUrl);
      // Title near the top, cover image centered in the remaining space.
      doc.setTextColor(...onAccent);
      doc.text(nameLines, W / 2, 44, { align: 'center' });
      const areaTop = 44 + (nameLines.length - 1) * lineH + 10;
      const areaBottom = bandTop - 8;
      const boxW = 96;
      const boxH = Math.min(74, areaBottom - areaTop);
      const scale = Math.min(boxW / img.width, boxH / img.height);
      const iw = img.width * scale;
      const ih = img.height * scale;
      const ix = (W - iw) / 2;
      const iy = areaTop + (areaBottom - areaTop - ih) / 2;
      doc.addImage(img.dataUrl, img.format, ix, iy, iw, ih);
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(1.2);
      doc.rect(ix, iy, iw, ih);
      drewCover = true;
    } catch {
      // cover failed to load — fall through to the centered layout
    }
  }

  if (!drewCover) {
    // Vertically center the album name in the free area.
    const blockH = (nameLines.length - 1) * lineH;
    const midY = (36 + bandTop - 6) / 2;
    doc.setTextColor(...onAccent);
    doc.text(nameLines, W / 2, midY - blockH / 2, { align: 'center' });
  }

  // Bottom band: album ID + subtitle, always legible (dark ink on the light band).
  doc.setTextColor(...onBand);
  doc.setFont('courier', 'normal');
  doc.setFontSize(10);
  doc.text(groupId(opts.albumId), W / 2, H - 19, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('End-to-end encrypted photo & video album', W / 2, H - 11, { align: 'center' });

  // ---------- Page 2: instructions ----------
  doc.addPage('a5', 'portrait');
  doc.setTextColor(...ink);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('How to open this album', W / 2, 30, { align: 'center' });

  doc.setTextColor(30, 30, 30);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  const steps = [
    '1. Scan the QR code on page 3 of this leaflet, or type the link printed beneath it into a browser.',
    '2. The album opens instantly - no account or password needed.',
    '3. The secret key is part of the link (after the # symbol). It never leaves your device: photos and videos are decrypted locally in your browser.',
    '4. Everything in this album is end-to-end encrypted (AES-256-GCM). Not even the storage servers can see the contents.',
    '5. Keep this leaflet private. Anyone who has it can view the album - and only those who have it can.',
  ];
  let y = 48;
  for (const s of steps) {
    const lines = doc.splitTextToSize(s, W - 36) as string[];
    doc.text(lines, 18, y);
    y += lines.length * 6 + 5;
  }

  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text('Album ID:', 18, y + 8);
  doc.setFont('courier', 'normal');
  doc.text(groupId(opts.albumId), 18, y + 15);

  // ---------- Page 3: QR code ----------
  doc.addPage('a5', 'portrait');
  if (colored) {
    doc.setDrawColor(...accent);
    doc.setLineWidth(1.2);
    doc.rect(12, 12, W - 24, H - 24);
  }
  doc.setTextColor(...ink);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Scan to view', W / 2, 32, { align: 'center' });

  const qrData = await QRCode.toDataURL(opts.link, {
    width: 800,
    margin: 0,
    errorCorrectionLevel: 'M',
    color: { dark: colored ? '#1f2937' : '#000000', light: '#ffffff' },
  });
  const qrSize = 88;
  doc.addImage(qrData, 'PNG', (W - qrSize) / 2, 42, qrSize, qrSize);

  // Link printed with explicit line spacing (no overlap), kept inside the frame.
  doc.setFont('courier', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(90, 90, 90);
  const linkLines = doc.splitTextToSize(opts.link, W - 36) as string[];
  let linkY = 144;
  for (const line of linkLines) {
    doc.text(line, W / 2, linkY, { align: 'center' });
    linkY += 4;
  }

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(70, 70, 70);
  doc.text(
    'The link contains the secret key - treat it like a password.',
    W / 2,
    Math.min(linkY + 10, H - 20),
    { align: 'center' },
  );

  // ---------- Page 4: notes ----------
  doc.addPage('a5', 'portrait');
  if (colored) {
    doc.setFillColor(...accent);
    doc.rect(0, 0, W, 26, 'F');
    doc.setTextColor(255, 255, 255);
  } else {
    doc.setTextColor(0, 0, 0);
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('Notes', W / 2, 17, { align: 'center' });

  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.3);
  for (let ly = 44; ly < H - 40; ly += 12) {
    doc.line(18, ly, W - 18, ly);
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(140, 140, 140);
  doc.text('Locket Album · private encrypted albums on Nostr', W / 2, H - 22, { align: 'center' });
  doc.text('Vibed with Marlowe · marlowe.shakespeare.wtf', W / 2, H - 16, { align: 'center' });

  const safe = name.replace(/[^\w-]+/g, '-').toLowerCase() || 'album';
  doc.save(`locket-${safe}-${colored ? 'color' : 'plain'}.pdf`);
}
